import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { isTablet } from 'react-native-device-info';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { ActivityIndicator } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { createTableHeadStyles } from '../../components/table/tableHeadStyles';
import { fs, iconSize, s, v } from '../../utils/libs';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  getSuppliersInformation,
  parseSuppliersResponse,
} from '../../api/backofficeSuppliers';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  appendSuppliersListData,
  setSuppliersListData,
  setSuppliersListError,
  setSuppliersListLoading,
  setSuppliersSearchTerm,
  type BackofficeSupplier,
} from '../../redux/backoffice/suppliersSlice';
import type { RootStackParamList } from '../../navigation/AppStack';

const BackOfficeSuppliersScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const { useSidebarNav } = useLayoutSize();
  const { width, height } = useWindowDimensions();
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );

  const suppliers = useAppSelector(
    state => state.backofficeSuppliers.list.items,
  );
  const loading = useAppSelector(
    state => state.backofficeSuppliers.list.loading,
  );
  const page = useAppSelector(state => state.backofficeSuppliers.list.page);
  const pageSize = useAppSelector(
    state => state.backofficeSuppliers.list.pageSize,
  );
  const hasNextPage = useAppSelector(
    state => state.backofficeSuppliers.list.hasNextPage,
  );
  const searchTerm = useAppSelector(
    state => state.backofficeSuppliers.list.searchTerm,
  );

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const formatFieldValue = useCallback((value?: string) => {
    const text = String(value || '').trim();
    return text.length > 0 ? text : '-';
  }, []);

  const headerRightNode = useMemo(
    () => (
      <Pressable
        onPress={() => navigation.navigate('BackOfficeAddSupplier')}
        style={({ pressed, hovered }: any) => [
          styles.headerCTA,
          pressed && styles.headerCTAPressed,
          Platform.OS === 'web' && hovered && styles.headerCTAHovered,
        ]}>
        <Text style={styles.headerCTAText}>+ Add New</Text>
      </Pressable>
    ),
    [navigation, styles],
  );

  const searchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: nativeHeaderSearchWidth,
        }}>
        <AppSearchBar
          placeholder="Search supplier by name"
          value={searchTerm}
          onChangeText={text => dispatch(setSuppliersSearchTerm(text))}
          onClear={() => dispatch(setSuppliersSearchTerm(''))}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={nativeHeaderSearchWidth}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [dispatch, nativeHeaderSearchWidth, searchTerm],
  );

  const webSearchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: width >= 768 && width < 1024 ? s(660) : s(620),
        }}>
        <AppSearchBarWeb
          placeholder="Search supplier by name"
          value={searchTerm}
          onChangeText={text => dispatch(setSuppliersSearchTerm(text))}
          onClear={() => dispatch(setSuppliersSearchTerm(''))}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={
            showTabletSidebarLayoutSetting && useSidebarNav
              ? width * 0.46
              : width * 0.32
          }
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [dispatch, searchTerm, showTabletSidebarLayoutSetting, useSidebarNav, width],
  );

  useLayoutEffect(() => {
    if (useSidebarNav) return;
    (navigation as any).setOptions({
      headerTitleContainerStyle: { flex: 1 },
      headerTitle: () => searchBar,
      headerRight: () => headerRightNode,
    });
  }, [headerRightNode, navigation, searchBar, useSidebarNav]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (navigation as any).navigate('BackOffice', {
              screen: 'Dashboard',
            });
          } catch {
            if (navigation.canGoBack()) navigation.goBack();
          }
        }}>
        {webSearchBar}
      </WebHeaderBar>
    );
  }, [headerRightNode, navigation, useSidebarNav, webSearchBar]);

  useWebHeader(webHeaderNode, Boolean(webHeaderNode));

  const fetchPage = useCallback(
    async (targetPage: number, append: boolean) => {
      if (!owner?.accessToken) return;
      try {
        if (!append) {
          dispatch(setSuppliersListLoading(true));
        }
        const res = await getSuppliersInformation(
          axiosBackoffice as any,
          targetPage,
          pageSize,
          undefined,
          { accessToken: owner.accessToken },
        );
        const parsed = parseSuppliersResponse(res);
        if (!parsed?.success) {
          dispatch(
            setSuppliersListError(
              parsed?.message || 'Failed to load suppliers',
            ),
          );
          return;
        }
        if (append) {
          dispatch(
            appendSuppliersListData({
              suppliers: parsed.suppliers || [],
              total: parsed.total || 0,
              page: parsed.page || targetPage,
              limit: parsed.limit || pageSize,
            }),
          );
          return;
        }
        dispatch(
          setSuppliersListData({
            suppliers: parsed.suppliers || [],
            total: parsed.total || 0,
            page: parsed.page || targetPage,
            limit: parsed.limit || pageSize,
          }),
        );
      } catch (error: any) {
        dispatch(
          setSuppliersListError(error?.message || 'Failed to load suppliers'),
        );
      }
    },
    [axiosBackoffice, dispatch, owner?.accessToken, pageSize],
  );

  useEffect(() => {
    if (!owner?.accessToken) return;
    fetchPage(1, false);
  }, [fetchPage, owner?.accessToken]);

  const filteredSuppliers = useMemo(() => {
    const query = String(searchTerm || '')
      .trim()
      .toLowerCase();
    if (!query) return suppliers;
    return suppliers.filter((supplier: BackofficeSupplier) => {
      const name = String(supplier?.bp_supplier_name || '').toLowerCase();
      const email = String(
        supplier?.bp_supplier_email_address || '',
      ).toLowerCase();
      const phone = String(
        supplier?.bp_supplier_phone_number || '',
      ).toLowerCase();
      const contact = String(
        supplier?.bp_supplier_contact_person_name || '',
      ).toLowerCase();
      return (
        name.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        contact.includes(query)
      );
    });
  }, [searchTerm, suppliers]);

  const handleLoadMore = useCallback(async () => {
    if (!owner?.accessToken) return;
    if (loading || isLoadingMore || !hasNextPage) return;
    setIsLoadingMore(true);
    try {
      await fetchPage(Number(page || 1) + 1, true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    fetchPage,
    hasNextPage,
    isLoadingMore,
    loading,
    owner?.accessToken,
    page,
  ]);

  const onRefresh = useCallback(async () => {
    if (refreshing || loading || !owner?.accessToken) return;

    setRefreshing(true);
    try {
      await fetchPage(1, false);
    } finally {
      setRefreshing(false);
    }
  }, [fetchPage, loading, owner?.accessToken, refreshing]);

  const renderItem = useCallback(
    ({ item, index }: { item: BackofficeSupplier; index: number }) => {
      const phoneNumber = String(item?.bp_supplier_phone_number || '').trim();
      const hasPhoneNumber = phoneNumber.length > 0;
      const supplierStatus = String(
        item?.bp_supplier_status || 'active',
      ).toLowerCase();
      const isActive = supplierStatus === 'active';

      return (
        <Pressable
          onPress={() =>
            navigation.navigate('BackOfficeSupplierDetails', {
              supplierId: String(item?._id || ''),
            })
          }
          style={({ pressed, hovered }: any) => [
            styles.rowCard,
            pressed && styles.rowCardPressed,
            Platform.OS === 'web' && hovered && styles.rowCardHovered,
          ]}>
          <View style={styles.rowNumberColumn}>
            <Text
              style={[
                styles.rowNumberText,
                !isActive && styles.rowNumberTextInactive,
              ]}>
              {index + 1}
            </Text>
          </View>
          <View style={styles.rowContent}>
            <View style={styles.rowTop}>
              <View style={styles.rowTitleWrap}>
                <View
                  style={[
                    styles.statusDot,
                    isActive ? styles.statusDotActive : styles.statusDotInactive,
                  ]}
                />
                <Text
                  style={[styles.rowTitle, !isActive && styles.rowTitleInactive]}
                  numberOfLines={1}>
                  {String(item?.bp_supplier_name || 'Unnamed supplier')}
                </Text>
                {!isActive && (
                  <Text style={styles.inactiveStatusLabel}>Inactive</Text>
                )}
              </View>
              <Text
                style={[styles.rowContactText, !isActive && styles.rowSubtextInactive]}
                numberOfLines={1}>
                {formatFieldValue(item?.bp_supplier_contact_person_name)}
              </Text>
            </View>
            <Text
              style={[
                styles.rowSubtext,
                hasPhoneNumber && styles.phoneLinkText,
                !isActive && styles.rowSubtextInactive,
              ]}
              numberOfLines={1}>
              {hasPhoneNumber ? phoneNumber : '—'}
            </Text>
          </View>
        </Pressable>
      );
    },
    [formatFieldValue, navigation, styles],
  );

  return (
    <View style={styles.container}>
      <View style={styles.listContainer}>
        <FlatList
          data={filteredSuppliers}
          keyExtractor={item => String(item?._id)}
          renderItem={renderItem}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.45}
          refreshing={refreshing}
          onRefresh={onRefresh}
          ListHeaderComponent={
            <View style={styles.listHeader}>
              <Text style={styles.listHeaderNumberText}>#</Text>
              <Text style={[styles.listHeaderText, styles.listHeaderNameText]}>
                Name
              </Text>
              <Text style={styles.listHeaderText}>Contact</Text>
            </View>
          }
          ListEmptyComponent={
            loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator />
                <Text style={styles.loadingText}>Loading suppliers...</Text>
              </View>
            ) : (
              <View style={styles.emptyWrap}>
                <MaterialCommunityIcons
                  name="truck-delivery-outline"
                  size={iconSize(64)}
                  color={theme.colors.textTertiary}
                />
                <Text style={styles.emptyTitle}>No suppliers found</Text>
                <Text style={styles.emptySubtext}>
                  Add a supplier & order products directly.
                </Text>
                <Pressable
                  onPress={() => navigation.navigate('BackOfficeAddSupplier')}
                  style={({ pressed, hovered }: any) => [
                    styles.emptyCTA,
                    pressed && styles.emptyCTAPressed,
                    Platform.OS === 'web' && hovered && styles.emptyCTAHovered,
                  ]}>
                  <Text style={styles.emptyCTAText}>+ Add supplier</Text>
                </Pressable>
              </View>
            )
          }
          ListFooterComponent={
            isLoadingMore ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator />
              </View>
            ) : null
          }
        />
      </View>
    </View>
  );
};

const createStyles = (theme: any) => {
  const tableHead = createTableHeadStyles(theme);
  return StyleSheet.create({
    container: {
      flex: 1,
      padding: s(5),
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    headerCTA: {
      marginRight: s(8),
      paddingHorizontal: s(10),
      paddingVertical: v(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    headerCTAHovered: {
      backgroundColor: 'rgba(255,255,255,0.14)',
    } as any,
    headerCTAPressed: {
      opacity: 0.75,
    },
    headerCTAText: {
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(16),
    },
    searchbar: {
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      marginBottom: v(10),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    listContainer: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      ...theme.shadows.md,
    },
    // Inventory's table head; the padding and "#" column match the rows
    listHeader: {
      ...tableHead.head,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: s(12),
    },
    listHeaderText: tableHead.headText,
    listHeaderNameText: {
      flex: 1,
    },
    listHeaderNumberText: {
      ...tableHead.headText,
      width: s(16),
      marginRight: s(10),
      textAlign: 'center',
    },
    rowCard: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      paddingHorizontal: s(12),
      paddingVertical: v(10),
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    rowCardHovered: {
      backgroundColor: theme.colors.surfaceSecondary,
    } as any,
    rowCardPressed: {
      opacity: 0.75,
    },
    rowNumberColumn: {
      width: s(16),
      marginRight: s(10),
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowNumberText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    rowContent: {
      flex: 1,
      minWidth: 0,
    },
    rowTop: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: v(4),
    },
    rowTitleWrap: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
    },
    statusDot: {
      width: s(7),
      height: s(7),
      marginRight: s(8),
      borderRadius: s(999),
    },
    statusDotActive: {
      backgroundColor: theme.colors.successEmerald,
    },
    statusDotInactive: {
      backgroundColor: theme.colors.errorDark,
    },
    rowTitle: {
      flex: 1,
      marginRight: s(8),
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    rowTitleInactive: {
      color: theme.colors.textSecondary,
    },
    inactiveStatusLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.textSecondary,
    },
    rowContactText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginLeft: s(12),
      minWidth: s(70),
      textAlign: 'right',
    },
    rowSubtext: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    rowSubtextInactive: {
      color: theme.colors.textTertiary,
    },
    rowNumberTextInactive: {
      color: theme.colors.textTertiary,
    },
    phoneLinkText: {
      color: theme.colors.primaryDark,
    },
    loadingWrap: {
      paddingVertical: v(36),
      alignItems: 'center',
    },
    loadingText: {
      marginTop: v(10),
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    emptyWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(20),
      paddingVertical: v(40),
      gap: v(10),
    },
    emptyTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    emptySubtext: {
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    emptyCTA: {
      marginTop: v(6),
      paddingHorizontal: s(20),
      paddingVertical: v(12),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.primary,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'opacity 0.15s ease-out',
      }),
    } as any,
    emptyCTAHovered: {
      opacity: 0.92,
    } as any,
    emptyCTAPressed: {
      opacity: 0.88,
    },
    emptyCTAText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(18),
      color: theme.colors.textInverse,
    },
    footerLoading: {
      paddingVertical: v(12),
      alignItems: 'center',
    },
  });
};

export default BackOfficeSuppliersScreen;
